import { derived, type Readable } from 'svelte/store';
import type { RouteName } from '$router';
import { BaseViewModel, type BaseViewModelInterface } from '$services';
import type {
	CurrentUser,
	ProjectData,
	UserTeamLiteData,
} from '@localekey/types';
import type { MenuOption } from '@shared/svelte';
import { toInitials } from '@shared/utils';

export type ProfileMenuOption = MenuOption | 'upgradePlan';

export type AppBarViewModelInterface = {
	readonly isLoggedIn: Readable<boolean>;
	readonly showProjectSelector: Readable<boolean>;
	readonly currentRoute: Readable<RouteName | undefined>;
	readonly showBackButton: Readable<boolean>;
	readonly isNavigating: Readable<boolean>;
	readonly currentTeam: Readable<UserTeamLiteData | undefined>;
	readonly currentUser: Readable<CurrentUser | undefined>;
	readonly currentUserPhotoURL: Readable<string | undefined>;
	readonly currentUserInitials: Readable<string | undefined>;
	readonly profileMenuOptions: Readable<ProfileMenuOption[]>;
	edit(): Promise<void>;
	goToPlanUpgrade(): Promise<void>;
} & BaseViewModelInterface;
class AppBarViewModel
	extends BaseViewModel
	implements AppBarViewModelInterface
{
	get showProjectSelector(): Readable<boolean> {
		return derived(this.currentRoute, ($currentRoute) => {
			if (!$currentRoute) {
				return false;
			}
			return ['project'].includes($currentRoute);
		});
	}

	get currentRoute(): Readable<RouteName | undefined> {
		return this.getR('routerService', 'currentRoute', undefined);
	}

	get isNavigating(): Readable<boolean> {
		return this.getR('routerService', 'isNavigating', false);
	}

	get showBackButton(): Readable<boolean> {
		return derived(this.currentRoute, ($currentRoute) => {
			if (!$currentRoute) {
				return false;
			}
			return ['editVideo', 'hubVideo'].includes($currentRoute);
		});
	}

	get currentTeam(): Readable<UserTeamLiteData | undefined> {
		return this.getR('authService', 'currentTeam', undefined);
	}
	get currentUser(): Readable<CurrentUser | undefined> {
		return this.getR('authService', 'currentUser', undefined);
	}
	get currentUserPhotoURL(): Readable<string | undefined> {
		return derived(
			this.currentUser,
			($currentUser) => $currentUser?.photoURL,
		);
	}
	get currentUserInitials(): Readable<string | undefined> {
		return derived(
			this.currentUser,
			($currentUser) =>
				$currentUser?.displayName &&
				toInitials($currentUser.displayName),
		);
	}

	get profileMenuOptions(): Readable<ProfileMenuOption[]> {
		return derived([this.t], ([$t]) => {
			const options: ProfileMenuOption[] = [
				{
					click: () => {
						void this.goToRoute({
							pathParameters: undefined,
							queryParameters: undefined,
							route: 'account',
						});
					},
					icon: 'gear',
					text: $t.settings(),
				},
				// 'localeMenu',
				// {
				// 	click: () => {
				// 		window.open(HELP_URL, '_blank');
				// 	},
				// 	icon: 'lifebuoy',
				// 	text: $t.help(),
				// },
				'divider',
			];

			options.push({
				click: async () => {
					void this.logEvent('logout', undefined);

					await this.execute(
						'authService',
						'setIsChangingAuthState',
						true,
					);

					const resOk = await this.execute('authService', 'signOut');

					if (resOk) {
						await this.execute(
							'teamService',
							'setCurrentTeam',
							undefined,
						);
						void (await this.execute('preferenceService', 'clear'));
						await this.goToRoute({
							pathParameters: undefined,
							queryParameters: undefined,
							route: 'login',
							setRedirectTo: true,
						});
					}
					await this.execute(
						'authService',
						'setIsChangingAuthState',
						false,
					);
				},
				icon: 'sign-out',
				text: $t.logout(),
			});

			return options;
		});
	}
	get isLoggedIn(): Readable<boolean> {
		return this.getR('authService', 'isLoggedIn', false);
	}

	async edit(): Promise<void> {
		const project = await this._getCurrentProject();
		await this.execute('projectService', 'setEditingProject', project);
		await this.execute('appService', 'setShowProjectFormDialog', true);
	}
	goToPlanUpgrade(): Promise<void> {
		throw new Error('Method not implemented.');
	}

	private async _getCurrentProject(): Promise<ProjectData> {
		const project = await this.get$('projectService', 'currentProject');
		if (!project) {
			throw new Error('No current project');
		}
		return project;
	}
}

export const getAppBarViewModel = (): AppBarViewModelInterface =>
	new AppBarViewModel({
		className: 'AppBarViewModel',
	});
